/**
 * OverlayProvider - Multi-Overlay Stack Management
 *
 * Provides a React context for managing a stack of overlays.
 * Supports focus trapping, z-index management, and keyboard navigation.
 */

import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ActionRegistryList } from '../../keyboard/actionRegistry';

let overlayTitleById: Map<string, string> | null = null;
function getOverlayTitleMap(): Map<string, string> {
  if (!overlayTitleById) {
    overlayTitleById = new Map<string, string>();
    for (const action of ActionRegistryList) {
      if (action.overlayId && action.overlayAction) {
        overlayTitleById.set(action.overlayId, action.title);
      }
    }
  }
  return overlayTitleById;
}

// =============================================================================
// Mobile Detection
// =============================================================================

const MOBILE_BREAKPOINT = 640; // px - matches Tailwind 'sm' breakpoint

/**
 * Hook to detect if the viewport is mobile-sized.
 * Uses matchMedia for efficient reactive updates.
 */
export function useMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= MOBILE_BREAKPOINT;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    setIsMobile(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent | MediaQueryList) => setIsMobile(e.matches);

    // Safari < 14 doesn't support addEventListener/removeEventListener on MediaQueryList.
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }

    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, []);

  return isMobile;
}

// =============================================================================
// Overlay Types
// =============================================================================

// All possible overlay IDs (matching TUI)
export type OverlayId =
  | 'help'
  | 'search'
  | 'goto'
  | 'settings'
  | 'genomeImport'
  | 'aaKey'
  | 'aaLegend'
  | 'comparison'
  | 'analysisMenu'
  | 'simulationHub'
  | 'simulationView'
  | 'complexity'
  | 'gcSkew'
  | 'bendability'
  | 'promoter'
  | 'repeats'
  | 'transcriptionFlow'
  | 'pressure'
  | 'selectionPressure'
  | 'modules'
  | 'hgt'
  | 'kmerAnomaly'
  | 'anomaly'
  | 'structureConstraint'
  | 'gel'
  | 'nonBDNA'
  | 'foldQuickview'
  | 'commandPalette'
  | 'hilbert'
  | 'phasePortrait'
  | 'biasDecomposition'
  | 'crispr'
  | 'synteny'
  | 'dotPlot'
  | 'tropism'
  | 'cgr'
  | 'stability'
  | 'welcome'
  | 'collaboration'
  | 'tour'
  | 'genomicSignaturePCA'
  | 'codonBias'
  | 'proteinDomains'
  | 'amgPathway'
  | 'codonAdaptation'
  | 'defenseArmsRace'
  | 'illustration'
  | 'prophageExcision'
  | 'mosaicRadar'
  | 'logo'
  | 'periodicity'
  | 'gpuWasmBenchmark'
  | 'cocktailCompatibility'
  | 'rnaStructure'
  | 'resistanceEvolution'
  | 'nicheNetwork'
  | 'phylodynamics'
  | 'epistasis'
  | 'environmentalProvenance'
  | 'latentSpaceAtlas'
  | 'hostInteractions'
  | 'pangenomeGraph';

function formatOverlayTitle(id: OverlayId): string {
  return getOverlayTitleMap().get(id) ?? id;
}

export interface OverlayConfig {
  id: OverlayId;
  blocking?: boolean; // Prevents interaction with content below
  closeOnEscape?: boolean;
  closeOnBackdrop?: boolean;
}

interface OverlayStackItem {
  id: OverlayId;
  config: OverlayConfig;
  zIndex: number;
}

interface StackLimitToast {
  message: string;
  expectedIds: string;
  undoStack: OverlayStackItem[];
}

interface OverlayContextValue {
  // State
  stack: OverlayStackItem[];
  topOverlay: OverlayId | null;
  isOpen: (id: OverlayId) => boolean;
  hasBlockingOverlay: boolean;
  /** Whether the viewport is mobile-sized */
  isMobile: boolean;

  // Actions
  open: (id: OverlayId, config?: Partial<OverlayConfig>) => void;
  close: (id?: OverlayId) => void;
  toggle: (id: OverlayId, config?: Partial<OverlayConfig>) => void;
  closeAll: () => void;

  // Overlay data store (for computed analysis data)
  overlayData: Record<string, unknown>;
  setOverlayData: (key: string, value: unknown) => void;
}

const OverlayContext = createContext<OverlayContextValue | null>(null);

// Base z-index must be higher than control deck (z-index: 300)
// Uses --z-modal (500) from CSS variables as base
const BASE_Z_INDEX = 500;
const MAX_STACK_SIZE = 3;

const DEFAULT_CONFIG: OverlayConfig = {
  id: 'help',
  blocking: true,
  closeOnEscape: true,
  closeOnBackdrop: true,
};

interface OverlayProviderProps {
  children: ReactNode;
}

export function OverlayProvider({ children }: OverlayProviderProps): React.ReactElement {
  const [stackState, setStackState] = useState<{
    stack: OverlayStackItem[];
    stackLimitToast: StackLimitToast | null;
  }>({
    stack: [],
    stackLimitToast: null,
  });
  const { stack, stackLimitToast } = stackState;
  const [overlayData, setOverlayDataState] = useState<Record<string, unknown>>({});
  const isMobile = useMobile();
  const stackLimitToastTimerRef = useRef<number | null>(null);

  const clearStackLimitToastTimer = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (stackLimitToastTimerRef.current) {
      window.clearTimeout(stackLimitToastTimerRef.current);
      stackLimitToastTimerRef.current = null;
    }
  }, []);

  const handleUndoStackLimitToast = useCallback(() => {
    clearStackLimitToastTimer();
    setStackState((current) => {
      const toast = current.stackLimitToast;
      if (!toast) return current;

      const currentIds = current.stack.map((item) => item.id).join('|');
      if (currentIds !== toast.expectedIds) {
        return { ...current, stackLimitToast: null };
      }

      return { ...current, stack: toast.undoStack, stackLimitToast: null };
    });
  }, [clearStackLimitToastTimer]);

  useEffect(() => {
    if (!stackLimitToast) return;
    if (typeof window === 'undefined') return;

    clearStackLimitToastTimer();
    stackLimitToastTimerRef.current = window.setTimeout(() => {
      setStackState((current) => {
        if (!current.stackLimitToast) return current;
        return { ...current, stackLimitToast: null };
      });
      stackLimitToastTimerRef.current = null;
    }, 8000);

    return clearStackLimitToastTimer;
  }, [clearStackLimitToastTimer, stackLimitToast]);

  // Get the top overlay
  const topOverlay = stack.length > 0 ? stack[stack.length - 1].id : null;

  // Check if any overlay is blocking
  const hasBlockingOverlay = stack.some(item => item.config.blocking);

  // Check if a specific overlay is open
  const isOpen = useCallback((id: OverlayId): boolean => {
    return stack.some(item => item.id === id);
  }, [stack]);

  // Open an overlay
  const open = useCallback((id: OverlayId, config?: Partial<OverlayConfig>) => {
    setStackState((current) => {
      const currentStack = current.stack;
      // Remove if already in stack (will re-add at top)
      const filtered = currentStack.filter(item => item.id !== id);
      const willEvict = filtered.length >= MAX_STACK_SIZE;
      const evicted = willEvict ? filtered[0] : null;
      const prevStack = currentStack;

      // Create new item
      const newItem: OverlayStackItem = {
        id,
        config: { ...DEFAULT_CONFIG, id, ...config },
        zIndex: BASE_Z_INDEX + filtered.length,
      };

      // Add to stack (limit to MAX_STACK_SIZE)
      const newStack = [...filtered, newItem].slice(-MAX_STACK_SIZE);

      // Update z-indices
      const normalized = newStack.map((item, index) => ({
        ...item,
        zIndex: BASE_Z_INDEX + index,
      }));

      if (!evicted) {
        return { ...current, stack: normalized };
      }

      // If we hit the stack cap, never fail silently. Use a toast with an "Undo" escape hatch.
      const evictedTitle = formatOverlayTitle(evicted.id);
      const openedTitle = formatOverlayTitle(id);
      const message = `Overlay limit (${MAX_STACK_SIZE}) — closed “${evictedTitle}” to open “${openedTitle}”.`;
      const expectedIds = normalized.map((item) => item.id).join('|');
      const undoStack = prevStack.map((item, index) => ({
        ...item,
        zIndex: BASE_Z_INDEX + index,
      }));

      return {
        ...current,
        stack: normalized,
        stackLimitToast: { message, expectedIds, undoStack },
      };
    });
  }, []);

  // Close an overlay (specific or top)
  const close = useCallback((id?: OverlayId) => {
    setStackState((current) => {
      if (id) {
        // Close specific overlay
        return { ...current, stack: current.stack.filter((item) => item.id !== id) };
      }

      // Close top overlay
      return { ...current, stack: current.stack.slice(0, -1) };
    });
  }, []);

  // Toggle an overlay
  const toggle = useCallback((id: OverlayId, config?: Partial<OverlayConfig>) => {
    if (isOpen(id)) {
      close(id);
    } else {
      open(id, config);
    }
  }, [isOpen, close, open]);

  // Close all overlays
  const closeAll = useCallback(() => {
    clearStackLimitToastTimer();
    setStackState((current) => ({ ...current, stack: [], stackLimitToast: null }));
  }, [clearStackLimitToastTimer]);

  // Set overlay data
  const setOverlayData = useCallback((key: string, value: unknown) => {
    setOverlayDataState(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Global escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (e.isComposing) return;
      if (e.defaultPrevented) return;
      if (stack.length === 0) return;

      const topItem = stack[stack.length - 1];
      if (topItem.config.closeOnEscape) {
        e.preventDefault();
        e.stopPropagation();
        close(topItem.id);
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [stack, close]);

  const value = useMemo<OverlayContextValue>(
    () => ({
      stack,
      topOverlay,
      isOpen,
      hasBlockingOverlay,
      isMobile,
      open,
      close,
      toggle,
      closeAll,
      overlayData,
      setOverlayData,
    }),
    [
      stack,
      topOverlay,
      isOpen,
      hasBlockingOverlay,
      isMobile,
      open,
      close,
      toggle,
      closeAll,
      overlayData,
      setOverlayData,
    ]
  );

  // React 19: Simplified context syntax (no .Provider needed)
  return (
    <OverlayContext value={value}>
      {children}
      {stackLimitToast && (
        <div className="toast toast-warning" role="status" aria-live="polite" data-testid="overlay-stack-limit-toast">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <span>{stackLimitToast.message}</span>
            <button className="btn btn-ghost btn-sm" type="button" onClick={handleUndoStackLimitToast} data-testid="overlay-stack-limit-undo">
              Undo
            </button>
          </div>
        </div>
      )}
    </OverlayContext>
  );
}

/**
 * Hook to access overlay context
 */
export function useOverlay(): OverlayContextValue {
  const context = useContext(OverlayContext);
  if (context) return context;
  // Safe no-op fallback to avoid crashes if a component mounts without provider
  const noop = () => {};
  return {
    stack: [],
    topOverlay: null,
    isOpen: () => false,
    hasBlockingOverlay: false,
    isMobile: false,
    open: noop,
    close: noop,
    toggle: noop,
    closeAll: noop,
    overlayData: {},
    setOverlayData: noop,
  };
}

/**
 * Hook to check if a specific overlay is at the top of the stack
 */
export function useIsTopOverlay(id: OverlayId): boolean {
  const { topOverlay } = useOverlay();
  return topOverlay === id;
}

/**
 * Hook to get overlay z-index
 */
export function useOverlayZIndex(id: OverlayId): number {
  const { stack } = useOverlay();
  const item = stack.find(s => s.id === id);
  return item?.zIndex ?? BASE_Z_INDEX;
}

export default OverlayProvider;
